import React, { useState, useEffect } from 'react';
import "./GoogleCalendar.css";
import { useCalendarSync } from '../hooks/useCalendarSync';

const API = "https://calendar.nareea.co.uk";

const DEFAULT_RANGES = {
  day:   { start: 8,  startMin: 0, end: 20, endMin: 0 },
  morn:  { start: 5,  startMin: 0, end: 12, endMin: 0 },
  eve:   { start: 12, startMin: 0, end: 18, endMin: 0 },
  late:  { start: 18, startMin: 0, end: 23, endMin: 0 },
  night: { start: 20, startMin: 0, end: 24, endMin: 0 },
};

function pad(n) { return String(n).padStart(2, '0'); }

function getWeekDates(offsetWeeks = 0) {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offsetWeeks * 7);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }
  return days;
}

function getMonthDates(offsetMonths = 0) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + offsetMonths;
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = first.getDay() === 0 ? 6 : first.getDay() - 1;
  const start = new Date(first);
  start.setDate(first.getDate() - startDay);
  const days = [];
  const d = new Date(start);
  while (d <= last || days.length % 7 !== 0) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
    if (days.length > 42) break;
  }
  return { days, month: new Date(year, month, 1) };
}

function formatDay(date) {
  return date.toLocaleDateString("en", { weekday: "short", day: "numeric" });
}

function formatWeekTitle(days) {
  const first = days[0];
  const last = days[6];
  return `${first.toLocaleDateString("en", { month: "long", day: "numeric" })} - ${last.toLocaleDateString("en", { month: "long", day: "numeric", year: "numeric" })}`;
}

function formatMonthTitle(monthDate) {
  return monthDate.toLocaleDateString("en", { month: "long", year: "numeric" });
}

function timeToHeight(startStr, endStr) {
  const [sh, sm] = startStr.split(":").map(Number);
  const [eh, em] = endStr.split(":").map(Number);
  return eh * 60 + em - (sh * 60 + sm);
}

function addBST(timeStr) {
  const [h, m] = timeStr.split(":").map(Number);
  const bh = (h + 1) % 24;
  return `${String(bh).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function hasConflict(event, allEvents) {
  return allEvents.some((other) => {
    if (other.uid === event.uid) return false;
    if (other.date !== event.date) return false;
    const s1 = event.startTime.replace(":", "");
    const e1 = event.endTime.replace(":", "");
    const s2 = other.startTime.replace(":", "");
    const e2 = other.endTime.replace(":", "");
    return s1 < e2 && s2 < e1;
  });
}

function formatBrief(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
}

function HourSettingsPanel({ ranges, onApprove, onBack }) {
  const [local, setLocal] = useState({ ...ranges });

  const updateRange = (key, field, value) => {
    setLocal(prev => ({ ...prev, [key]: { ...prev[key], [field]: parseInt(value) || 0 } }));
  };

  const rangeLabels = {
    day:   'Day',
    morn:  'Morning',
    eve:   'Evening',
    late:  'Late Eve',
    night: 'Night',
  };

  const inputStyle = {
    width: 44, padding: '5px 4px', border: '1px solid #dadce0',
    borderRadius: 4, fontSize: 13, textAlign: 'center', outline: 'none',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 560, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a3e', marginBottom: 4 }}>Hour Range Settings</div>
        <div style={{ fontSize: 13, color: '#5f6368', marginBottom: 22 }}>Customize time ranges for each view mode</div>

        {Object.entries(rangeLabels).map(([key, label]) => {
          const r = local[key];
          const previewStart = `${pad(r.start)}:${pad(r.startMin)}`;
          const previewEnd = `${pad(r.end)}:${pad(r.endMin)}`;
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, padding: '10px 16px', background: '#f8f9fa', borderRadius: 8, width: '100%', boxSizing: 'border-box' }}>
              <div style={{ width: 70, fontWeight: 600, fontSize: 13, color: '#3c4043', flexShrink: 0 }}>{label}</div>
              <span style={{ fontSize: 11, color: '#5f6368', flexShrink: 0 }}>From</span>
              <input type="number" min="0" max="23" value={r.start} onChange={e => updateRange(key, 'start', e.target.value)} style={inputStyle} />
              <span style={{ color: '#9aa0a6', fontSize: 14 }}>:</span>
              <input type="number" min="0" max="59" step="15" value={r.startMin} onChange={e => updateRange(key, 'startMin', e.target.value)} style={inputStyle} />
              <span style={{ fontSize: 11, color: '#5f6368', flexShrink: 0, margin: '0 2px' }}>–</span>
              <input type="number" min="0" max="24" value={r.end} onChange={e => updateRange(key, 'end', e.target.value)} style={inputStyle} />
              <span style={{ color: '#9aa0a6', fontSize: 14 }}>:</span>
              <input type="number" min="0" max="59" step="15" value={r.endMin} onChange={e => updateRange(key, 'endMin', e.target.value)} style={inputStyle} />
              <div style={{ fontSize: 12, color: '#1a73e8', fontWeight: 700, flexShrink: 0, width: 96, textAlign: 'right', marginLeft: 8 }}>
                {previewStart} – {previewEnd}
              </div>
            </div>
          );
        })}

        <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
          <button onClick={onBack} style={{ padding: '10px 28px', border: '1px solid #dadce0', borderRadius: 8, background: '#fff', color: '#3c4043', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Back</button>
          <button onClick={() => onApprove(local)} style={{ padding: '10px 28px', border: 'none', borderRadius: 8, background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Approve</button>
        </div>
      </div>
    </div>
  );
}

const GoogleCalendarView = ({ conflictResolved, resolution }) => {
  const [viewType, setViewType] = useState('week');
  const [weekOffset, setWeekOffset] = useState(1);
  const [monthOffset, setMonthOffset] = useState(0);
  const [activeHour, setActiveHour] = useState('24h');
  const [prevActiveHour, setPrevActiveHour] = useState('24h');
  const [showSettings, setShowSettings] = useState(false);
  const [ranges, setRanges] = useState({ ...DEFAULT_RANGES });
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncFlash, setSyncFlash] = useState(false);
  const [resolvedConflicts, setResolvedConflicts] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [meetingBrief, setMeetingBrief] = useState('');
  const [briefLoading, setBriefLoading] = useState(false);
  const [panelPos, setPanelPos] = useState({ x: window.innerWidth - 340, y: 100 });
  const dragging = React.useRef(false);
  const dragOffset = React.useRef({ x: 0, y: 0 });

  const fetchEvents = () => {
    fetch(`${API}/api/draft/latest`)
      .then((r) => r.json())
      .then((data) => { setAllEvents(data.events || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchEvents(); }, []);

  useCalendarSync((msg) => {
    if (msg.type === 'EVENT_RESCHEDULED' || msg.type === 'CALENDAR_UPDATED') {
      fetchEvents();
      setSyncFlash(true);
      setTimeout(() => setSyncFlash(false), 2000);
    }
  });

  const onMouseDown = (e) => {
    dragging.current = true;
    dragOffset.current = { x: e.clientX - panelPos.x, y: e.clientY - panelPos.y };
    e.preventDefault();
  };

  const onMouseMove = React.useCallback((e) => {
    if (!dragging.current) return;
    setPanelPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
  }, []);

  const onMouseUp = React.useCallback(() => { dragging.current = false; }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setMeetingBrief('');
    setBriefLoading(true);
    fetch(`${API}/api/meeting-brief`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, allEvents }),
    })
      .then(r => r.json())
      .then(data => { setMeetingBrief(data.brief || ''); setBriefLoading(false); })
      .catch(() => { setMeetingBrief('Brief unavailable.'); setBriefLoading(false); });
  };

  const handleAccept = (uid) => setResolvedConflicts((prev) => ({ ...prev, [uid]: "accepted" }));
  const handleDismiss = (uid) => setResolvedConflicts((prev) => ({ ...prev, [uid]: "dismissed" }));

  const navigate = (dir) => {
    if (viewType === 'week') setWeekOffset(w => w + dir);
    else setMonthOffset(m => m + dir);
  };

  const goToday = () => { setWeekOffset(1); setMonthOffset(0); };

  const handleHourBtn = (key) => {
    if (key === 'set') {
      setPrevActiveHour(activeHour);
      setShowSettings(true);
    } else {
      setActiveHour(key);
    }
  };

  const handleSettingsApprove = (newRanges) => {
    setRanges(newRanges);
    setActiveHour('24h');
    setShowSettings(false);
  };

  const handleSettingsBack = () => {
    setActiveHour(prevActiveHour);
    setShowSettings(false);
  };

  const getHourRange = () => {
    if (activeHour === '24h') return { startTotal: 0, endTotal: 24 * 60 };
    const r = ranges[activeHour];
    if (!r) return { startTotal: 0, endTotal: 24 * 60 };
    return { startTotal: r.start * 60 + r.startMin, endTotal: r.end * 60 + r.endMin };
  };

  const { startTotal, endTotal } = getHourRange();

  const days = getWeekDates(weekOffset);
  const { days: monthDays, month: monthDate } = getMonthDates(monthOffset);

  const weekEventsFiltered = allEvents.filter((event) =>
    days.some((d) => d.toISOString().split("T")[0] === event.date)
  );

  const conflicts = weekEventsFiltered.filter(
    (e) => hasConflict(e, weekEventsFiltered) && !resolvedConflicts[e.uid] && !conflictResolved,
  );

  const isConflictAccepted = conflictResolved || Object.values(resolvedConflicts).includes("accepted");
  const isConflictDismissed = Object.values(resolvedConflicts).includes("dismissed") && !isConflictAccepted;
  const isConflictGone = isConflictAccepted || isConflictDismissed;

  const title = viewType === 'week'
    ? formatWeekTitle(days)
    : viewType === 'month'
    ? formatMonthTitle(monthDate)
    : `${formatMonthTitle(getMonthDates(monthOffset).month)} — ${formatMonthTitle(getMonthDates(monthOffset + 2).month)}`;

  const hourBtns = [
    { key: '24h',  label: '24h'   },
    { key: 'day',  label: 'Day'   },
    { key: 'morn', label: 'Morn'  },
    { key: 'eve',  label: 'Eve'   },
    { key: 'late', label: 'Late'  },
    { key: 'night',label: 'Night' },
    { key: 'set',  label: '⚙' },
  ];

  const timeSlots = [];
  for (let m = startTotal; m < endTotal; m += 60) {
    timeSlots.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`);
  }

  const renderMonthGrid = (offsetAdd) => {
    const { days: mDays, month: mDate } = getMonthDates(monthOffset + offsetAdd);
    return (
      <div>
        <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 14, color: '#1a73e8', marginBottom: 8 }}>{formatMonthTitle(mDate)}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, marginBottom: 4 }}>
          {['M','T','W','T','F','S','S'].map((d, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: 10, color: '#5f6368', fontWeight: 600 }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
          {mDays.map((date, i) => {
            const dateStr = date.toISOString().split("T")[0];
            const isCurrentMonth = date.getMonth() === mDate.getMonth();
            const isToday = dateStr === new Date().toISOString().split("T")[0];
            const dayEvents = allEvents.filter(e => e.date === dateStr);
            return (
              <div key={i} style={{ minHeight: 52, background: isCurrentMonth ? '#fff' : '#f8f9fa', border: '1px solid #e8eaed', borderRadius: 3, padding: '2px 3px', opacity: isCurrentMonth ? 1 : 0.4 }}>
                <div style={{ fontSize: 10, fontWeight: isToday ? 700 : 400, color: isToday ? '#fff' : '#3c4043', background: isToday ? '#1a73e8' : 'none', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 1 }}>
                  {date.getDate()}
                </div>
                {dayEvents.slice(0, 2).map((event, ei) => (
                  <div key={ei} onClick={() => handleEventClick(event)} style={{ fontSize: 9, padding: '1px 3px', marginBottom: 1, borderRadius: 2, cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', background: event.priority === 'critical' ? '#fce8e6' : '#e6f4ea', color: event.priority === 'critical' ? '#c5221f' : '#137333', borderLeft: `2px solid ${event.priority === 'critical' ? '#ea4335' : '#34a853'}` }}>
                    {event.title}
                  </div>
                ))}
                {dayEvents.length > 2 && <div style={{ fontSize: 8, color: '#5f6368' }}>+{dayEvents.length - 2}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="google-calendar-container">

      {showSettings && (
        <HourSettingsPanel ranges={ranges} onApprove={handleSettingsApprove} onBack={handleSettingsBack} />
      )}

      {selectedEvent && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setSelectedEvent(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 520, width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: '#5f6368', fontWeight: 600, letterSpacing: 1, marginBottom: 4 }}>MEETING BRIEF</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a3e' }}>{selectedEvent.title}</div>
                <div style={{ fontSize: 13, color: '#5f6368', marginTop: 2 }}>
                  {selectedEvent.date} · {selectedEvent.startTime}–{selectedEvent.endTime}
                  {selectedEvent.location ? ` · ${selectedEvent.location}` : ''}
                </div>
                {selectedEvent.meet_link && (
                  <a href={selectedEvent.meet_link} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 6, fontSize: 12, color: '#1a73e8', fontWeight: 600 }}>Join Google Meet</a>
                )}
              </div>
              <button onClick={() => setSelectedEvent(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#5f6368', lineHeight: 1 }}>×</button>
            </div>
            {briefLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 0', gap: 10 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#1a73e8', animation: `mbDot 1.2s ease-in-out ${i*0.2}s infinite` }} />
                  ))}
                </div>
                <div style={{ fontSize: 13, color: '#5f6368' }}>Preparing meeting brief...</div>
                <style>{`@keyframes mbDot{0%,80%,100%{transform:scale(0.7);opacity:0.4}40%{transform:scale(1.2);opacity:1}}`}</style>
              </div>
            ) : (
              <div style={{ fontSize: 13, lineHeight: 1.7, color: '#3c4043', background: '#f8f9fa', borderRadius: 8, padding: 14 }}
                dangerouslySetInnerHTML={{ __html: formatBrief(meetingBrief) }} />
            )}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setSelectedEvent(null)} style={{ background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      <div className="calendar-header">
        <div className="header-left">
          <button className="hamburger-menu">☰</button>
          <span className="calendar-title">Calendar</span>
          {loading && <span style={{ fontSize: 12, color: "#5f6368" }}>Loading...</span>}
          {!loading && <span style={{ fontSize: 11, color: "#34a853" }}>{allEvents.length} events loaded</span>}
          {syncFlash && <span style={{ fontSize: 11, color: '#1a73e8', fontWeight: 700, marginLeft: 6 }}>↻ updated</span>}
        </div>
        <div className="header-center">
          <button className="nav-btn" onClick={() => navigate(-1)}>←</button>
          <h2 className="week-title">{title}</h2>
          <button className="nav-btn" onClick={() => navigate(1)}>→</button>
          <button className="today-btn" onClick={goToday}>Today</button>
        </div>
        <div className="header-right">
          {viewType === 'week' && (
            <div style={{ display: 'flex', gap: 2, marginRight: 8 }}>
              {hourBtns.map(btn => (
                <button
                  key={btn.key}
                  className={`view-btn ${activeHour === btn.key ? 'active' : ''}`}
                  onClick={() => handleHourBtn(btn.key)}
                  style={{ fontSize: 11, padding: '4px 7px' }}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          )}
          <button className={`view-btn ${viewType === 'week' ? 'active' : ''}`} onClick={() => setViewType('week')}>Week</button>
          <button className={`view-btn ${viewType === 'month' ? 'active' : ''}`} onClick={() => setViewType('month')}>Month</button>
          <button className={`view-btn ${viewType === '3months' ? 'active' : ''}`} onClick={() => setViewType('3months')}>3M</button>
          
        </div>
      </div>

      {viewType === 'week' && (
        <div className="calendar-grid">
          <div className="time-column">
            <div className="time-header">BST</div>
            {timeSlots.map((t, i) => (
              <div key={i} className="time-slot">{t}</div>
            ))}
          </div>
          <div className="days-grid">
            {days.map((date, idx) => {
              const dateStr = date.toISOString().split("T")[0];
              const dayEvents = weekEventsFiltered
                .filter((e) => e.date === dateStr)
                .filter((e) => {
                  const [h, m] = e.startTime.split(":").map(Number);
                  return (h * 60 + m) >= startTotal && (h * 60 + m) < endTotal;
                });
              return (
                <div key={idx} className="day-column">
                  <div className="day-header">{formatDay(date)}</div>
                  <div className="events-container">
                    {dayEvents.map((event, ei) => {
                      const [h, m] = event.startTime.split(":").map(Number);
                      const top = (h * 60 + m) - startTotal;
                      const height = Math.max(timeToHeight(event.startTime, event.endTime), 30);
                      const isConflicting = hasConflict(event, weekEventsFiltered);
                      const resolved = resolvedConflicts[event.uid];
                      return (
                        <div
                          key={ei}
                          className={`event ${event.priority}-event`}
                          style={{ top: `${top + 60}px`, height: `${height}px`, cursor: 'pointer' }}
                          onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                        >
                          <div className="event-time">{addBST(event.startTime)} - {addBST(event.endTime)}</div>
                          <div className="event-title">{event.title}</div>
                          {event.location && <div className="event-attendees">{event.location}</div>}
                          {isConflicting && !resolved && !isConflictGone && (
                            <div className="urgent-conflict">
                              <div className="conflict-alert">Time conflict</div>
                              <div className="conflict-actions">
                                <button className="conflict-btn accept" onClick={(e) => { e.stopPropagation(); handleAccept(event.uid); }}>Accept</button>
                                <button className="conflict-btn reschedule" onClick={(e) => { e.stopPropagation(); handleDismiss(event.uid); }}>Dismiss</button>
                              </div>
                            </div>
                          )}
                          {resolved === "accepted" && <div className="resolved-badge">Conflict resolved</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewType === 'month' && (
        <div style={{ flex: 1, overflow: 'auto', padding: '0 8px 8px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, marginBottom: 1 }}>
            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#5f6368', padding: '8px 0' }}>{d}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {monthDays.map((date, i) => {
              const dateStr = date.toISOString().split("T")[0];
              const isCurrentMonth = date.getMonth() === monthDate.getMonth();
              const isToday = dateStr === new Date().toISOString().split("T")[0];
              const dayEvents = allEvents.filter(e => e.date === dateStr);
              return (
                <div key={i} style={{ minHeight: 100, background: isCurrentMonth ? '#fff' : '#f8f9fa', border: '1px solid #e8eaed', borderRadius: 4, padding: '4px 6px', opacity: isCurrentMonth ? 1 : 0.5 }}>
                  <div style={{ fontSize: 13, fontWeight: isToday ? 700 : 400, color: isToday ? '#fff' : isCurrentMonth ? '#3c4043' : '#9aa0a6', background: isToday ? '#1a73e8' : 'none', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 2 }}>
                    {date.getDate()}
                  </div>
                  {dayEvents.slice(0, 3).map((event, ei) => (
                    <div key={ei} onClick={() => handleEventClick(event)} style={{ fontSize: 11, padding: '2px 5px', marginBottom: 2, borderRadius: 3, cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', background: hasConflict(event, allEvents) ? '#fce8e6' : event.source === 'google' ? '#e8f0fe' : '#e6f4ea', color: hasConflict(event, allEvents) ? '#c5221f' : event.source === 'google' ? '#1a73e8' : '#137333', borderLeft: `3px solid ${hasConflict(event, allEvents) ? '#ea4335' : event.source === 'google' ? '#1a73e8' : '#34a853'}` }}>
                      {event.startTime} {event.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && <div style={{ fontSize: 10, color: '#5f6368', padding: '1px 4px' }}>+{dayEvents.length - 3} more</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewType === '3months' && (
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
          {[0, 1, 2].map(offset => (
            <div key={offset}>{renderMonthGrid(offset)}</div>
          ))}
        </div>
      )}

      {viewType === 'week' && conflicts.length > 0 && !isConflictGone && (
        <div className="ai-panel" style={{ left: panelPos.x, top: panelPos.y, right: "auto" }} onMouseDown={onMouseDown}>
          <div className="ai-header"><span>AI Calendar Assistant</span></div>
          <div className="ai-recommendations">
            {conflicts.slice(0, 2).map((event, i) => (
              <div key={i} className="recommendation critical">
                <div className="rec-priority">CONFLICT</div>
                <div className="rec-text"><strong>{event.title}</strong> overlaps with another event at {event.startTime}.</div>
                <div className="rec-actions">
                  <button className="apply-btn" onClick={() => handleAccept(event.uid)}>Resolve</button>
                  <button className="dismiss-btn" onClick={() => handleDismiss(event.uid)}>Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarView;
